import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { DOMParser as PMMDOMParser } from '@tiptap/pm/model';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import api from '../utils/api';
import EditorToolbar from '../components/EditorToolbar';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { markdownToHtml } from '../utils/markdownUtils';
import { ArrowLeft, Trash2, WifiOff, Check } from 'lucide-react';
import FileAttachments from '../components/FileAttachments';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';

const CATEGORY_PATH = {
  archive: '/archive',
  news: '/news',
  random: '/random',
  product: '/product',
};

const COLORS = ['#f87171', '#fb923c', '#facc15', '#4ade80', '#22d3ee', '#818cf8', '#e879f9', '#f472b6'];
function getUserColor(id) { return COLORS[(id || 0) % COLORS.length]; }

function cursorBuilder(user) {
  const cursor = document.createElement('span');
  cursor.className = 'yjs-cursor';
  cursor.style.borderColor = user.color;
  const label = document.createElement('span');
  label.className = 'yjs-cursor-label';
  label.style.backgroundColor = user.color;
  label.textContent = user.name;
  cursor.appendChild(label);
  return cursor;
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/posts/upload', fd);
  return r.data.url;
}

export default function PostCollabPage({ category, title: pageTitle }) {
  const { theme } = useTheme();
  const tagColor = (color) => theme === 'light' ? getContrastColor(color) : 'white';
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');

  const [post, setPost] = useState(null);
  const [titleValue, setTitleValue] = useState('');
  const [peers, setPeers] = useState([]);
  const [connected, setConnected] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState(null);

  const fileInputRef = useRef(null);
  const mdFileInputRef = useRef(null);
  const contentSaveTimer = useRef(null);
  const titleSaveTimer = useRef(null);
  // 시딩이 완료된 후에만 auto-save 허용
  const readyToSave = useRef(false);
  // 두 비동기 이벤트(API 응답 + Yjs sync) 모두 완료 후 시딩
  const postDataRef = useRef(null);
  const yjsSyncedRef = useRef(false);
  const seededRef = useRef(false);
  const editorRef = useRef(null);

  const basePath = CATEGORY_PATH[category] || `/${category}`;

  // ── Yjs 설정 ──
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const isDev = import.meta.env.DEV;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = isDev ? `${window.location.hostname}:3800` : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `post-${id}`, doc, {
      params: { token },
    });
    return { ydoc: doc, provider: prov };
  }, [id]);

  const YjsExt = useMemo(() => {
    const fragment = ydoc.getXmlFragment('content');
    const undoMgr = new Y.UndoManager(fragment);
    return Extension.create({
      name: 'yjs-post',
      addProseMirrorPlugins() {
        return [
          ySyncPlugin(fragment),
          yCursorPlugin(provider.awareness, { cursorBuilder }),
          yUndoPlugin({ undoManager: undoMgr }),
        ];
      },
    });
  }, [ydoc, provider]);

  // 두 조건이 모두 충족될 때 1번만 시딩
  // frag.length 대신 editor.isEmpty 사용:
  // ySyncPlugin이 초기화 시 빈 fragment에도 <paragraph>를 추가해서 length가 항상 1 이상임
  const tryToSeed = useCallback(() => {
    if (seededRef.current) return;
    if (!postDataRef.current || !yjsSyncedRef.current || !editorRef.current) return;

    if (editorRef.current.isEmpty && postDataRef.current.content) {
      editorRef.current.commands.setContent(postDataRef.current.content, false);
    }
    seededRef.current = true;
    readyToSave.current = true;
  }, []);

  // ── 에디터 ──
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExt,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    autofocus: false,
    onUpdate: ({ editor }) => {
      // 시딩 완료 전 Yjs 초기 sync에 의한 onUpdate는 무시 (빈 내용 저장 방지)
      if (!readyToSave.current) return;
      clearTimeout(contentSaveTimer.current);
      setSaveStatus('saving');
      contentSaveTimer.current = setTimeout(async () => {
        try {
          await api.put(`/posts/${id}`, { content: editor.getHTML() });
          setSaveStatus('saved');
        } catch {
          setSaveStatus('error');
        }
      }, 2000);
    },
    editorProps: {
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        files.forEach(async (f) => {
          const url = await uploadImage(f);
          view.dispatch(view.state.tr.replaceSelectionWith(
            view.state.schema.nodes.image.create({ src: url })
          ));
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const img = items.find(i => i.type.startsWith('image/'));
        if (!img) return false;
        event.preventDefault();
        const file = img.getAsFile();
        (async () => {
          const url = await uploadImage(file);
          view.dispatch(view.state.tr.replaceSelectionWith(
            view.state.schema.nodes.image.create({ src: url })
          ));
        })();
        return true;
      },
    },
  }, [ydoc]);

  // editorRef 동기화 (tryToSeed에서 사용)
  useEffect(() => {
    editorRef.current = editor;
    if (editor) tryToSeed();
  }, [editor, tryToSeed]);

  // ── DB에서 post 로드 → tryToSeed 시도 ──
  useEffect(() => {
    // id가 바뀌면 상태 초기화
    readyToSave.current = false;
    seededRef.current = false;
    yjsSyncedRef.current = false;
    postDataRef.current = null;

    api.get(`/posts/${id}`).then(r => {
      setPost(r.data);
      setTitleValue(r.data.title);
      setSelectedTagId(r.data.tagId || null);
      postDataRef.current = r.data;
      tryToSeed();
    });
    api.get('/posts/tags', { params: { forCategory: category } }).then(r => setTags(r.data));
  }, [id, tryToSeed]);

  // ── Yjs sync 이벤트 → tryToSeed 시도 ──
  useEffect(() => {
    const onSync = (isSynced) => {
      if (!isSynced) return;
      yjsSyncedRef.current = true;
      tryToSeed();
    };
    provider.on('sync', onSync);
    // 이미 synced 상태일 수도 있음 (provider.synced = y-websocket 내장 프로퍼티)
    if (provider.synced) {
      yjsSyncedRef.current = true;
      tryToSeed();
    }
    return () => provider.off('sync', onSync);
  }, [provider, tryToSeed]);

  // ── 접속상태 + awareness + 정리 ──
  useEffect(() => {
    // 'connecting' 이벤트는 무시 — false로 바뀌면 아이콘이 깜빡임
    const onStatus = ({ status }) => {
      if (status === 'connected') setConnected(true);
      else if (status === 'disconnected') setConnected(false);
    };
    provider.on('status', onStatus);

    provider.awareness.setLocalStateField('user', {
      name: user.name || user.username,
      color: getUserColor(user.id),
    });

    const onAwareness = () => {
      const seen = new Set();
      const list = [];
      provider.awareness.getStates().forEach((state) => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          list.push({ ...state.user });
        }
      });
      const myName = user.name || user.username;
      const idx = list.findIndex(u => u.name === myName);
      if (idx >= 0) list[idx].isMe = true;
      else list.unshift({ name: myName, color: getUserColor(user.id), isMe: true });
      setPeers(list);
    };
    provider.awareness.on('change', onAwareness);
    onAwareness();

    return () => {
      provider.off('status', onStatus);
      provider.awareness.off('change', onAwareness);
      clearTimeout(contentSaveTimer.current);
      clearTimeout(titleSaveTimer.current);
      provider.destroy();
      ydoc.destroy();
    };
  }, [provider, ydoc]);

  // ── 제목 자동 저장 ──
  const scheduleTitleSave = useCallback((t) => {
    clearTimeout(titleSaveTimer.current);
    titleSaveTimer.current = setTimeout(async () => {
      if (t.trim()) await api.put(`/posts/${id}`, { title: t }).catch(() => {});
    }, 1500);
  }, [id]);

  // ── 태그 변경 즉시 저장 ──
  const handleTagChange = async (tagId) => {
    setSelectedTagId(tagId);
    await api.put(`/posts/${id}`, { tagId: tagId || null }).catch(() => {});
  };

  // ── 삭제 ──
  const canDelete = post && (post.authorId === user.id || user.role === 'superadmin');
  const remove = async () => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/posts/${id}`);
    navigate(basePath);
  };

  // ── 이미지 ──
  const handleImageButton = () => fileInputRef.current?.click();
  const handleMdUpload = () => mdFileInputRef.current?.click();
  const handleMdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const text = await file.text();
    const html = markdownToHtml(text);
    const view = editor.view;
    const parser = PMMDOMParser.fromSchema(view.state.schema);
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const slice = parser.parseSlice(dom, { preserveWhitespace: false });
    const tr = view.state.tr.replaceSelection(slice);
    view.dispatch(tr.scrollIntoView());
    e.target.value = '';
  };
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length || !editor) return;
    for (const f of files) {
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  if (!post) return <div className="text-gray-600 text-sm p-8">불러오는 중...</div>;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 상단 바 */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <button
          onClick={() => navigate(basePath)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft size={15} /> {pageTitle} 목록
        </button>

        <div className="flex items-center gap-3">
          {saveStatus === 'saving' && <span className="text-gray-600 text-xs">저장 중...</span>}
          {saveStatus === 'saved' && (
            <span className="flex items-center gap-1 text-gray-600 text-xs">
              <Check size={11} /> 저장됨
            </span>
          )}
          {saveStatus === 'error' && <span className="text-red-500 text-xs">저장 실패</span>}

          {peers.length > 0 && (
            <div className="flex -space-x-1.5">
              {peers.slice(0, 6).map((p, i) => (
                <div
                  key={i}
                  title={p.isMe ? `${p.name} (나)` : p.name}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${p.isMe ? 'border-brand' : 'border-gray-900'}`}
                  style={{ backgroundColor: p.color, color: tagColor(p.color) }}
                >
                  {p.name?.charAt(0)}
                </div>
              ))}
            </div>
          )}

          {/* null = 초기 로딩중이므로 아이콘 숨김, false = 실제 연결 끊김 */}
          {connected === false && (
            <WifiOff size={14} className="text-red-400" title="연결 끊김" />
          )}

          {canDelete && (
            <button
              onClick={remove}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-gray-500 hover:text-red-400 text-sm rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Trash2 size={13} /> 삭제
            </button>
          )}
        </div>
      </div>

      {/* 에디터 카드 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
        <div className="border-b border-gray-800 px-6 py-4 flex-shrink-0">
          <input
            value={titleValue}
            onChange={e => {
              setTitleValue(e.target.value);
              scheduleTitleSave(e.target.value);
            }}
            placeholder="제목"
            className="w-full bg-transparent text-white text-2xl font-bold focus:outline-none placeholder-gray-700 mb-2"
          />
          {tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => handleTagChange(null)}
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  !selectedTagId ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white border border-gray-700'
                }`}
              >
                없음
              </button>
              {tags.map(t => (
                <button
                  key={t.id}
                  onClick={() => handleTagChange(t.id)}
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                    selectedTagId === t.id ? 'opacity-100' : 'opacity-40 hover:opacity-70'
                  }`}
                  style={{ backgroundColor: t.color, color: tagColor(t.color) }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          <EditorToolbar editor={editor} onImageUpload={handleImageButton} onMarkdownUpload={handleMdUpload} />
        </div>

        <div className="flex-1 overflow-y-auto">
          <EditorContent editor={editor} />
        </div>

        <FileAttachments entityType="post" entityId={post?.id} editor={editor} />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <input ref={mdFileInputRef} type="file" accept=".md,text/markdown" className="hidden" onChange={handleMdFile} />
      </div>
    </div>
  );
}
